import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function BusinessProfileTab({ user, centerData, onRefresh }) {
  const [form, setForm] = useState({
    businessName: '', phone: '', email: '', website: '',
    description: '', address: '', city: '', state: 'MD', zip: '',
    hours: DAYS.reduce((a, d) => ({ ...a, [d]: { open: true, from: '08:00', to: '17:00' } }), {}),
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (centerData) {
      setForm(f => ({
        ...f,
        businessName: centerData.businessName || '',
        phone:        centerData.phone || '',
        email:        centerData.email || '',
        website:      centerData.website || '',
        description:  centerData.description || '',
        address:      centerData.address || '',
        city:         centerData.city || '',
        state:        centerData.state || 'MD',
        zip:          centerData.zip || '',
        hours:        centerData.hours || f.hours,
      }));
      setLogoPreview(centerData.logoUrl || null);
    }
  }, [centerData]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const setHour = (day, field, val) => setForm(f => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], [field]: val } } }));

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      let logoUrl = centerData?.logoUrl || null;
      if (logoFile) {
        const r = ref(storage, `serviceCenters/${user.uid}/logo`);
        await uploadBytes(r, logoFile);
        logoUrl = await getDownloadURL(r);
      }
      await updateDoc(doc(db, 'serviceCenters', user.uid), {
        ...form, logoUrl, updatedAt: serverTimestamp(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onRefresh) onRefresh();
    } catch (e) { alert('Error saving: ' + e.message); }
    finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111827', background: '#f9fafb', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ padding: 32, maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Business Profile</h1>
          <p style={{ color: '#6b7280', marginTop: 4 }}>This information appears to customers on the CocoWash platform</p>
        </div>
        <button onClick={save} disabled={saving} style={{ background: saved ? '#16a34a' : '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Logo */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Business Logo</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ width: 80, height: 80, borderRadius: 40, background: '#fff7ed', border: '2px solid #F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 28, fontWeight: 900, color: '#F97316' }}>{(form.businessName || 'B').charAt(0)}</span>}
          </div>
          <div>
            <label style={{ display: 'inline-block', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Upload Logo
              <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
            </label>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>PNG or JPG, recommended 400x400px</p>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Basic Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div><label style={labelStyle}>Business Name</label><input style={inputStyle} value={form.businessName} onChange={e => set('businessName', e.target.value)} placeholder="e.g. Downtown Auto Spa" /></div>
          <div><label style={labelStyle}>Phone Number</label><input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(301) 555-0100" /></div>
          <div><label style={labelStyle}>Email Address</label><input style={inputStyle} value={form.email} onChange={e => set('email', e.target.value)} placeholder="info@yourbusiness.com" /></div>
          <div><label style={labelStyle}>Website (optional)</label><input style={inputStyle} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://yourbusiness.com" /></div>
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={labelStyle}>Business Description</label>
          <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell customers about your business, experience, and specialties..." />
        </div>
      </div>

      {/* Address */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Primary Location</h3>
        <div style={{ marginBottom: 12 }}><label style={labelStyle}>Street Address</label><input style={inputStyle} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main Street" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>City</label><input style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Gaithersburg" /></div>
          <div><label style={labelStyle}>State</label><input style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)} placeholder="MD" /></div>
          <div><label style={labelStyle}>ZIP Code</label><input style={inputStyle} value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="20879" /></div>
        </div>
      </div>

      {/* Hours */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>Business Hours</h3>
        {DAYS.map(day => (
          <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 16, paddingVertical: 8, borderBottom: '1px solid #f3f4f6', padding: '10px 0' }}>
            <div style={{ width: 100, fontSize: 13, fontWeight: 600, color: '#374151' }}>{day}</div>
            <input type="checkbox" checked={form.hours[day]?.open ?? true} onChange={e => setHour(day, 'open', e.target.checked)} style={{ accentColor: '#F97316' }} />
            {form.hours[day]?.open ? (
              <>
                <input type="time" value={form.hours[day]?.from || '08:00'} onChange={e => setHour(day, 'from', e.target.value)} style={{ ...inputStyle, width: 120 }} />
                <span style={{ color: '#6b7280' }}>to</span>
                <input type="time" value={form.hours[day]?.to || '17:00'} onChange={e => setHour(day, 'to', e.target.value)} style={{ ...inputStyle, width: 120 }} />
              </>
            ) : (
              <span style={{ fontSize: 13, color: '#9ca3af' }}>Closed</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}